// Code-aware error copy for feed mutations. The feed mutation hooks
// (lib/mutations/feed.ts) all funnel non-2xx through `throwApiError`, which
// re-throws a `DomainError` carrying the server's `{ code, message?, details? }`
// envelope. Reading `err.message` surfaced the raw machine code to the user
// (e.g. "feed/empty_post"); this maps the known feed codes to translated copy,
// summarises VALIDATION field errors, and falls back to a caller-supplied key.
//
// Local to the feed feature on purpose — `lib/api-error.ts` is the shared
// envelope-narrowing surface and stays code-agnostic. The friendly copy is a
// product concern that belongs next to the screens that show it.

import { type useTranslations } from 'use-intl';
import {
  parseApiErrorCode,
  parseApiErrorDetails,
} from '../../lib/api-error';

type FeedTranslate = ReturnType<typeof useTranslations<'Feed'>>;

// Per-field VALIDATION summary, e.g. "body: Too long". The field names are
// technical identifiers, not user copy, so this stays untranslated.
function validationSummary(err: unknown): string | null {
  const details = parseApiErrorDetails(err);
  if (details === null) return null;
  const parts = Object.entries(details).map(
    ([field, messages]) => `${field}: ${messages.join(', ')}`,
  );
  return parts.length > 0 ? parts.join('; ') : null;
}

// Maps the feed error codes the server emits to their catalog key under
// `errors.codes.*`. Codes absent here fall through to the caller's fallback.
const FEED_CODE_KEY = {
  'feed/empty_post': 'errors.codes.feed/empty_post',
  'feed/not_author': 'errors.codes.feed/not_author',
  'feed/already_deleted': 'errors.codes.feed/already_deleted',
  'feed/parent_comment_mismatch': 'errors.codes.feed/parent_comment_mismatch',
  'feed/membership_revoked': 'errors.codes.feed/membership_revoked',
  'feed/attachment_not_visible': 'errors.codes.feed/attachment_not_visible',
  'feed/invalid_media': 'errors.codes.feed/invalid_media',
  'feed/unsupported_media_type': 'errors.codes.feed/unsupported_media_type',
  'feed/idempotency_key_reused': 'errors.codes.feed/idempotency_key_reused',
  NOT_FOUND: 'errors.codes.NOT_FOUND',
  FORBIDDEN: 'errors.codes.FORBIDDEN',
  CONFLICT: 'errors.codes.CONFLICT',
  RATE_LIMITED: 'errors.codes.RATE_LIMITED',
} as const;

// Maps a thrown feed-mutation error to translated user-facing copy. Never
// returns a raw machine code: known codes get curated copy, VALIDATION gets a
// field summary, everything else gets the translated `fallbackKey`.
export function feedErrorMessage(
  err: unknown,
  t: FeedTranslate,
  fallbackKey: Parameters<FeedTranslate>[0],
): string {
  const code = parseApiErrorCode(err);
  if (code !== null) {
    const key = FEED_CODE_KEY[code as keyof typeof FEED_CODE_KEY];
    if (key !== undefined) return t(key);
    if (code === 'VALIDATION') {
      return validationSummary(err) ?? t(fallbackKey);
    }
  }
  return t(fallbackKey);
}
